/**
 * Shared NDJSON (Newline-Delimited JSON) stream reader.
 *
 * Handles fetch with cookie-based auth (BetterAuth HttpOnly cookies sent
 * via `credentials: 'include'`), TextDecoder buffering, line splitting,
 * JSON parsing, heartbeat filtering, and error event detection. Used by
 * study session response submission, revision chat, and any other
 * streaming endpoint.
 */
import { API_BASE_URL } from '@/shared/api/client';
import { handleSessionExpired } from '@/shared/api/sessionExpired';
import { isSigningOut } from '@/shared/api/signOutFlag';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface StreamNdjsonOptions<T> {
  /** Called for each parsed event (heartbeats and error events are excluded). */
  onEvent: (event: T) => void;
  /** Called when the server sends an `{ event: 'error', message }` payload. */
  onError?: (message: string) => void;
  /** Extra headers merged into the request (after auth and content-type). */
  headers?: Record<string, string>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Parse a single NDJSON line and dispatch to the appropriate callback. */
function processLine<T>(line: string, opts: StreamNdjsonOptions<T>): void {
  if (!line.trim()) return;
  try {
    const parsed = JSON.parse(line) as Record<string, unknown>;
    if (parsed.event === 'error') {
      opts.onError?.(typeof parsed.message === 'string' ? parsed.message : 'Unknown error');
    } else if (parsed.event !== 'heartbeat') {
      opts.onEvent(parsed as T);
    }
  } catch {
    // Skip malformed lines
  }
}

// ─── Main function ──────────────────────────────────────────────────────────

/**
 * Open a streaming NDJSON connection and dispatch parsed events to callbacks.
 *
 * @param url   API path (appended to `API_BASE_URL`), e.g. `/me/chat/revision/123/messages`
 * @param init  Standard `RequestInit` (method, body, signal, etc.)
 * @param opts  Event callbacks and optional extra headers
 */
export async function streamNdjson<T>(
  url: string,
  init: RequestInit,
  opts: StreamNdjsonOptions<T>
): Promise<void> {
  const baseUrl = API_BASE_URL.replace(/\/+$/, '');

  const response = await fetch(`${baseUrl}${url}`, {
    ...init,
    // BetterAuth HttpOnly cookies are sent automatically; no Authorization
    // header. credentials: 'include' is required because this endpoint may
    // be cross-subdomain in production (formency.app → api.formency.app).
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      ...init.headers,
      ...opts.headers,
    },
  });

  if (!response.ok) {
    // Mid-session 401 (cookie expired during a study session) — bounce to
    // /login the same way the axios client does. Without this, the user
    // sees a generic streaming error and stays on a half-loaded page.
    if (response.status === 401 && !isSigningOut()) {
      handleSessionExpired();
    }
    const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
    throw new Error((err as { message?: string }).message || `HTTP ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null — streaming not supported');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop()!;
    for (const line of lines) {
      processLine(line, opts);
    }
  }

  // Flush any remaining content in the decoder and buffer
  buffer += decoder.decode();
  if (buffer.trim()) {
    processLine(buffer.trim(), opts);
  }
}
